import { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'
import { REGEXP_ONLY_DIGITS } from 'input-otp'
import { toast } from 'sonner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useSendVerificationEmail,
  useVerifyEmail,
} from '@/shared/hooks/api/emailVerification'
import { useGetUserSession } from '@/shared/hooks/api/auth'

const VERIFICATION_CODE_LENGTH = 6
const RESEND_COOLDOWN_SECONDS = 30

type OnboardingEmailVerificationStepProps = {
  email: string
  isBusy: boolean
  onVerified: () => void
}

export function OnboardingEmailVerificationStep({
  email,
  isBusy,
  onVerified,
}: OnboardingEmailVerificationStepProps) {
  const { t } = useTranslation('onboarding')
  const [resendCodeButtonDisabled, setResendCodeButtonDisabled] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [verificationCode, setVerificationCode] = useState('')
  const [verificationError, setVerificationError] = useState<string | null>(
    null
  )
  const hasSentInitialCode = useRef(false)
  const resendCooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  )

  const { data: session, isPending: isSessionPending } = useGetUserSession()
  const {
    mutateAsync: sendEmailVerificationCode,
    isPending: isSendingEmailVerificationCode,
  } = useSendVerificationEmail()
  const {
    mutateAsync: verifyEmailWithCode,
    isPending: isVerifyingEmailWithCode,
  } = useVerifyEmail()

  const isEmailVerified = session?.emailVerifiedAt != null
  const isLoading =
    isBusy || isVerifyingEmailWithCode || isSendingEmailVerificationCode
  const isInvalid = verificationError !== null
  const isCodeComplete =
    verificationCode.length === VERIFICATION_CODE_LENGTH

  function startResendCooldown(seconds: number = RESEND_COOLDOWN_SECONDS) {
    if (resendCooldownTimerRef.current) {
      clearInterval(resendCooldownTimerRef.current)
    }
    setResendCodeButtonDisabled(true)
    setCountdown(seconds)
    resendCooldownTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (resendCooldownTimerRef.current) {
            clearInterval(resendCooldownTimerRef.current)
            resendCooldownTimerRef.current = null
          }
          setResendCodeButtonDisabled(false)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  useEffect(() => {
    return () => {
      if (resendCooldownTimerRef.current) {
        clearInterval(resendCooldownTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (isSessionPending || isEmailVerified || hasSentInitialCode.current) {
      return
    }
    hasSentInitialCode.current = true

    sendEmailVerificationCode()
      .then(() => {
        toast.success(t('emailVerification.toast.codeSent'))
        startResendCooldown()
      })
      .catch((error) => {
        if (error instanceof AxiosError) {
          const message = error.response?.data?.message as string | undefined
          if (message === 'EMAIL_VERIFICATION_COOLDOWN') {
            const retryAfter = error.response?.data?.details
              ?.retryAfterSeconds as number | undefined
            startResendCooldown(
              typeof retryAfter === 'number' && retryAfter > 0
                ? retryAfter
                : RESEND_COOLDOWN_SECONDS
            )
            return
          }
          if (message === 'EMAIL_ALREADY_VERIFIED') {
            onVerified()
            return
          }
        }
        toast.error(t('emailVerification.toast.genericError'))
        console.error('Error sending verification code:', error)
      })
  }, [
    isEmailVerified,
    isSessionPending,
    onVerified,
    sendEmailVerificationCode,
    t,
  ])

  const handleSendCode = async () => {
    setVerificationError(null)
    try {
      await sendEmailVerificationCode()
      toast.success(t('emailVerification.toast.codeSent'))
      setVerificationCode('')
      startResendCooldown()
    } catch (error) {
      if (error instanceof AxiosError) {
        const message = error.response?.data?.message as string | undefined
        if (message === 'EMAIL_VERIFICATION_COOLDOWN') {
          const retryAfter = error.response?.data?.details
            ?.retryAfterSeconds as number | undefined
          startResendCooldown(
            typeof retryAfter === 'number' && retryAfter > 0
              ? retryAfter
              : RESEND_COOLDOWN_SECONDS
          )
          return
        }
        if (message === 'EMAIL_ALREADY_VERIFIED') {
          onVerified()
          return
        }
      }
      toast.error(t('emailVerification.toast.genericError'))
      console.error('Error sending verification code:', error)
    }
  }

  const handleVerifyCode = async () => {
    if (isEmailVerified) {
      onVerified()
      return
    }
    if (!isCodeComplete) {
      return
    }
    setVerificationError(null)
    try {
      await verifyEmailWithCode(verificationCode)
      onVerified()
    } catch (error) {
      let errorMessage = t('emailVerification.genericError')
      if (error instanceof AxiosError) {
        const message = error.response?.data?.message as string | undefined
        if (message === 'INVALID_VERIFICATION_CODE') {
          errorMessage = t('emailVerification.invalidCodeError')
        } else if (message === 'VERIFICATION_CODE_EXPIRED') {
          errorMessage = t('emailVerification.expiredCodeError')
        } else if (message === 'EMAIL_ALREADY_VERIFIED') {
          onVerified()
          return
        }
      }
      setVerificationError(errorMessage)
    }
  }

  const handleVerificationCodeChange = (value: string) => {
    setVerificationCode(value)
    setVerificationError(null)
  }

  if (isSessionPending) {
    return (
      <Card className='w-full'>
        <CardContent className='space-y-4 pt-6'>
          <Skeleton className='mx-auto h-4 w-full max-w-sm' />
          <Skeleton className='mx-auto h-10 w-56' />
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card className='w-full'>
        <CardContent className='space-y-6'>
          {!isEmailVerified ? (
            <div className='flex flex-col items-center gap-6 pt-2'>
              <p className='max-w-sm text-center text-sm leading-relaxed text-muted-foreground'>
                <Trans
                  i18nKey='emailVerification.sentTo'
                  ns='onboarding'
                  values={{ email }}
                  components={{
                    email: (
                      <span className='font-medium text-foreground' />
                    ),
                  }}
                />
              </p>

              <div className='flex w-full flex-col items-center gap-4'>
                <Label htmlFor='verification-code' className='sr-only'>
                  {t('emailVerification.codeInputLabel')}
                </Label>
                <InputOTP
                  id='verification-code'
                  maxLength={VERIFICATION_CODE_LENGTH}
                  pattern={REGEXP_ONLY_DIGITS}
                  value={verificationCode}
                  onChange={handleVerificationCodeChange}
                  disabled={isLoading}
                  aria-invalid={isInvalid}
                >
                  <InputOTPGroup>
                    {Array.from(
                      { length: VERIFICATION_CODE_LENGTH },
                      (_, index) => (
                        <InputOTPSlot
                          key={index}
                          index={index}
                          aria-invalid={isInvalid}
                        />
                      )
                    )}
                  </InputOTPGroup>
                </InputOTP>

                <div className='flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-sm text-muted-foreground'>
                  <span>{t('emailVerification.didntReceiveCode')}</span>
                  <Button
                    type='button'
                    variant='link'
                    className='h-auto px-0'
                    onClick={handleSendCode}
                    disabled={isLoading || resendCodeButtonDisabled}
                  >
                    {resendCodeButtonDisabled
                      ? t('emailVerification.actions.resendCodeIn', {
                          countdown,
                        })
                      : t('emailVerification.actions.resendCode')}
                  </Button>
                </div>
              </div>

              {verificationError && (
                <Alert variant='destructive' className='w-full'>
                  <AlertDescription>{verificationError}</AlertDescription>
                </Alert>
              )}
            </div>
          ) : (
            <Alert variant='success'>
              <AlertDescription>
                {t('emailVerification.success')}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
      <div className='mt-6 flex justify-end'>
        <Button
          type='button'
          onClick={handleVerifyCode}
          disabled={isLoading || (!isEmailVerified && !isCodeComplete)}
        >
          {t('continue')}
        </Button>
      </div>
    </>
  )
}
